/**
 * Paywall = pricing-table-style picker.
 *
 * - Authenticated users without an active subscription land here automatically
 *   (ProtectedRoute -> renders <Paywall /> when billing.has_access === false).
 * - The page is ALSO reachable as /paywall?plan=monthly|yearly. If a plan is
 *   pre-selected, we highlight it but the user is still free to flip the
 *   toggle. Clicking "Continue" hands off to a Stripe Checkout Session for the
 *   selected plan.
 *
 * Note on Stripe-side plan switching: Stripe Checkout does not natively allow
 * users to switch monthly<->yearly inside the hosted checkout form. The
 * pre-checkout pricing-table-style picker here gives the same effective UX -
 * you change your mind ON THIS PAGE, then Continue redirects to Stripe for the
 * chosen plan. To replace this page with an *embedded* Stripe Pricing Table
 * widget later, set REACT_APP_STRIPE_PRICING_TABLE_ID + REACT_APP_STRIPE_PUBLISHABLE_KEY.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, LogOut, Check, Sparkles, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import MarketingHeader from "@/components/MarketingHeader";

const PLANS = [
  {
    id: "monthly",
    headline: "Monthly",
    price: 25,
    period: "per month",
    note: "Billed monthly. Cancel anytime.",
    badge: null,
  },
  {
    id: "yearly",
    headline: "Yearly",
    price: 240,
    period: "per year",
    note: "Billed once a year. €20/mo equivalent - save 20%.",
    badge: "Save 20%",
  },
];

const FEATURES = [
  "Unlimited Gmail / Outlook / SMTP inboxes",
  "Unlimited campaigns, sequences & leads",
  "AI Sequence Analyzer (Claude Sonnet 4.5)",
  "Multi-inbox Shuffle Bag rotation + jitter",
  "Warmup engine + DNS health checker",
  "A/B variants + live analytics",
  "Full PDF audit exports",
];

export default function Paywall() {
  const { user, billing, logout, refreshBilling } = useAuth();
  const [params] = useSearchParams();
  const [plan, setPlan] = useState("yearly");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  const emailVerified = billing?.email_verified !== false; // default-true if unknown

  // Pre-select plan from URL if provided. Falls back to "yearly".
  useEffect(() => {
    const p = params.get("plan");
    if (p === "monthly" || p === "yearly") setPlan(p);
  }, [params]);

  const subscribe = async () => {
    if (!emailVerified) {
      toast.error("Please confirm your email first.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not start checkout - please try again.",
      );
      setBusy(false);
    }
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success(`Verification link sent to ${user?.email}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not resend - try again in a moment.",
      );
    } finally {
      setResending(false);
    }
  };

  const recheck = async () => {
    setResending(true);
    try {
      await refreshBilling();
    } finally {
      setResending(false);
    }
  };

  const signOut = (
    <button
      onClick={logout}
      data-testid="paywall-logout"
      className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-400 hover:text-white inline-flex items-center gap-1.5"
    >
      <LogOut className="w-3 h-3" strokeWidth={1.5} />
      Sign out
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <MarketingHeader
        showSubscribe={false}
        showSignIn={false}
        rightSlot={signOut}
      />
      <main className="flex-1 max-w-5xl mx-auto px-6 py-16 w-full">
        {!emailVerified ? (
          <div
            className="max-w-xl mx-auto text-center"
            data-testid="paywall-verify-gate"
          >
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
              <Mail className="w-6 h-6 text-amber-300" strokeWidth={1.6} />
            </div>
            <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-mono">
              One last step
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 leading-tight">
              Check your email.
            </h1>
            <p className="text-neutral-400 text-sm mt-5 leading-relaxed">
              We sent a confirmation link to{" "}
              <span className="text-white font-medium">{user?.email}</span>. Click
              it to activate your account before subscribing.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={resendVerification}
                disabled={resending}
                data-testid="paywall-resend-verification"
                className="inline-flex items-center justify-center gap-2 bg-white text-black px-5 py-3 rounded-md text-sm font-medium hover:bg-neutral-200 disabled:opacity-50"
              >
                {resending ? (
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
                ) : (
                  <Mail className="w-4 h-4" strokeWidth={1.8} />
                )}
                Resend email
              </button>
              <button
                onClick={recheck}
                disabled={resending}
                data-testid="paywall-recheck-verification"
                className="inline-flex items-center justify-center gap-2 border border-[#27272a] bg-[#0d0d0d] text-white px-5 py-3 rounded-md text-sm hover:bg-[#161616] disabled:opacity-50"
              >
                <RefreshCw className="w-4 h-4" strokeWidth={1.8} />
                I clicked the link
              </button>
            </div>
            <p className="text-xs text-neutral-600 mt-8 leading-relaxed">
              Not in your inbox? Check your spam folder. The link is good for
              24 hours.
            </p>
          </div>
        ) : (
          <>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-mono inline-flex items-center gap-2">
                <Sparkles className="w-3 h-3" strokeWidth={1.8} />
                One step left
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-4 leading-tight">
                Pick your plan.
              </h1>
              <p className="text-neutral-400 text-sm mt-4 max-w-xl mx-auto leading-relaxed">
                Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}. Switch
                between monthly and yearly anytime below - your choice carries
                into checkout.
              </p>
            </div>

        <div className="mt-12 grid md:grid-cols-2 gap-4">
          {PLANS.map((p) => {
            const active = plan === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setPlan(p.id)}
                data-testid={`paywall-plan-${p.id}`}
                className={`text-left border rounded-2xl p-8 transition-colors group relative ${
                  active
                    ? "border-white bg-[#161616]"
                    : "border-[#1a1a1a] bg-[#0d0d0d] hover:bg-[#111]"
                }`}
              >
                {p.badge && (
                  <div className="absolute -top-2.5 left-6 text-[10px] uppercase tracking-[0.2em] font-mono bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 rounded-full px-2.5 py-1">
                    {p.badge}
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-mono uppercase tracking-[0.22em] text-neutral-500">
                      {p.headline}
                    </div>
                    <div className="mt-4 flex items-baseline gap-2">
                      <div className="font-display text-5xl font-semibold">
                        €{p.price}
                      </div>
                      <div className="text-neutral-500 text-xs font-mono">
                        {p.period}
                      </div>
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-mono mt-3">
                      {p.note}
                    </div>
                  </div>
                  <div
                    className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                      active
                        ? "border-white bg-white"
                        : "border-[#27272a] bg-[#0a0a0a]"
                    }`}
                  >
                    {active && <Check className="w-3 h-3 text-black" strokeWidth={2.5} />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-8 flex flex-col items-center gap-4">
          <button
            onClick={subscribe}
            disabled={busy}
            data-testid="paywall-continue-button"
            className="w-full sm:w-auto min-w-[280px] flex items-center justify-center gap-2 bg-white text-black px-8 py-4 rounded-md font-medium text-sm hover:bg-neutral-200 disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
            ) : (
              <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            )}
            {busy ? "Redirecting to Stripe…" : "Continue"}
          </button>
          <p className="text-xs text-neutral-600 leading-relaxed text-center max-w-md">
            Stripe handles the payment. You can change your card or cancel from
            the billing portal anytime.
          </p>
        </div>

        <div className="mt-16 border-t border-[#161616] pt-10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
            Either plan includes
          </div>
          <ul className="mt-5 grid sm:grid-cols-2 gap-2.5 text-sm text-neutral-300">
            {FEATURES.map((b) => (
              <li key={b} className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" strokeWidth={1.8} />
                {b}
              </li>
            ))}
          </ul>
        </div>
          </>
        )}
      </main>
    </div>
  );
}
